import React, { useState, useEffect } from "react";
import axios, { AxiosResponse } from "axios";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Spinner,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    FormGroup,
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import TableContainer from "../../Components/Common/TableContainer";
import moment from "moment";

interface ExpeditionLine {
    id: number;
    product_id: number;
    ref: string;
    libelle: string;
    qty: number;
    qty_shipped: number;
    subprice: number;
    tva_tx: number;
    origin_line_id: number;
}

interface Expedition {
    id: number;
    ref: string;
    status: number;
    status_name: string;
    date_creation: string;
    date_valid: string;
    date_delivery: string;
    date_expedition: string;
    socid: number;
    company_name: string;
    code_client: string;
    order_id: number;
    order_ref: string;
    order_ref_customer: string;
    order_date: string;
    user_login: string;
    nb_lines: number;
    note_public: string;
    note_private: string;
    lines: ExpeditionLine[];
    orderinfo: {
        orderid: number;
        ref: string;
        ref_client: string;
        client_name: string;
        date_creation: string;
    };
    type: 'comptoir' | 'franchise';
}

interface SmartPaginationProps {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    countPerPage: number;
    onPageChange: (page: number) => void;
    loading: boolean;
}

const SmartPagination: React.FC<SmartPaginationProps> = ({
    currentPage,
    totalPages,
    totalCount,
    countPerPage,
    onPageChange,
    loading
}) => {
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    const [pageInput, setPageInput] = useState(currentPage.toString());

    useEffect(() => {
        setPageInput(currentPage.toString());
    }, [currentPage]);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    const handlePageJump = () => {
        const newPage = parseInt(pageInput);
        if (!isNaN(newPage)) {
            const clampedPage = Math.max(1, Math.min(totalPages, newPage));
            onPageChange(clampedPage);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handlePageJump();
        }
    };

    return (
        <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
                Affichage <strong>{countPerPage}</strong> expédition(s) sur <strong>{totalCount.toLocaleString()}</strong>
            </div>
            
            <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center gap-1">
                    <Input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{ width: '60px' }}
                        className="text-center"
                        bsSize="sm"
                        disabled={loading || totalPages <= 1}
                    />
                    <Button
                        color="primary"
                        size="sm"
                        onClick={handlePageJump}
                        disabled={loading || totalPages <= 1}
                    >
                        {loading ? <Spinner size="sm" /> : 'Aller'}
                    </Button>
                </div>
                
                <div className="btn-group">
                    <Button
                        size="sm"
                        color="light"
                        disabled={currentPage === 1 || loading}
                        onClick={() => onPageChange(1)}
                    >
                        <i className="ri-skip-back-line"></i>
                    </Button>
                    <Button
                        size="sm"
                        color="light"
                        disabled={currentPage === 1 || loading}
                        onClick={() => onPageChange(currentPage - 1)}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </Button>
                    <Button
                        size="sm"
                        color="light"
                        disabled
                        className="disabled"
                    >
                        Page {currentPage} / {totalPages}
                    </Button>
                    <Button
                        size="sm"
                        color="light"
                        disabled={currentPage === totalPages || loading}
                        onClick={() => onPageChange(currentPage + 1)}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </Button>
                    <Button
                        size="sm"
                        color="light"
                        disabled={currentPage === totalPages || loading}
                        onClick={() => onPageChange(totalPages)}
                    >
                        <i className="ri-skip-forward-line"></i>
                    </Button>
                </div>
            </div>
        </div>
    );
};

interface ExpeditionDetailsModalProps {
    isOpen: boolean;
    toggle: () => void;
    expedition: Expedition | null;
}

const ExpeditionDetailsModal: React.FC<ExpeditionDetailsModalProps> = ({
    isOpen,
    toggle,
    expedition
}) => {
    const getStatusBadge = (status: number) => {
        switch (status) {
            case 0: return <Badge color="secondary">Brouillon</Badge>;
            case 1: return <Badge color="success">Validée</Badge>;
            case 2: return <Badge color="info">Clôturée</Badge>;
            case -1: return <Badge color="danger">Annulée</Badge>;
            default: return <Badge color="secondary">Inconnu</Badge>;
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === "") return "Non définie";
        return moment(dateString).format('DD/MM/YYYY HH:mm');
    };

    const getTypeBadge = (type: string) => {
        switch (type) {
            case 'comptoir': return <Badge color="primary">Comptoir</Badge>;
            case 'franchise': return <Badge color="warning">Franchise</Badge>;
            default: return null;
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                <i className="ri-truck-line me-2"></i>
                Détails de l'expédition
            </ModalHeader>
            <ModalBody>
                {expedition && (
                    <div className="expedition-details">
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h4 className="mb-1">{expedition.ref}</h4>
                                    <div className="d-flex align-items-center gap-2">
                                        {getStatusBadge(expedition.status)}
                                        {getTypeBadge(expedition.type)}
                                        <span className="text-muted">
                                            Créée le {expedition.date_creation}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-bold">{expedition.company_name}</div>
                                    <small className="text-muted">Client: {expedition.code_client}</small>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <h6 className="border-bottom pb-2 mb-3">
                                <i className="ri-shopping-cart-2-line me-2"></i>
                                Commande associée
                            </h6>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-2">
                                        <small className="text-muted">Référence commande</small>
                                        <div className="fw-semibold">{expedition.order_ref}</div>
                                    </div>
                                    <div className="mb-2">
                                        <small className="text-muted">Date commande</small>
                                        <div className="fw-semibold">{expedition.order_date}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-2">
                                        <small className="text-muted">Référence client</small>
                                        <div className="fw-semibold">{expedition.code_client|| 'Non définie'}</div>
                                    </div>
                                    <div className="mb-2">
                                        <small className="text-muted">Type</small>
                                        <div className="fw-semibold">
                                            {expedition.type === 'comptoir' ? 'Comptoir' : 'Franchise'}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <div className="mb-4">
                            <h6 className="border-bottom pb-2 mb-3">
                                <i className="ri-list-check-2 me-2"></i>
                                Articles expédiés ({expedition.nb_lines} ligne(s))
                            </h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Référence</th>
                                            <th>Désignation</th>
                                            <th className="text-end">Quantité commandée</th>
                                            <th className="text-end">Expédiée</th>
                                            <th className="text-end">Prix Unitaire</th>
                                            <th className="text-end">TVA</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expedition.lines && expedition.lines.length > 0 ? (
                                            expedition.lines.map((line) => (
                                                <tr key={line.id || Math.random()}>
                                                    <td>
                                                        <div className="fw-semibold">{line.ref || 'N/A'}</div>
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '250px' }}>
                                                            {line.libelle || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="fw-semibold">{line.qty || 0}</div>
                                                    </td>
                                                    <td className="text-end">
                                                        <Badge color={parseInt(line.qty_shipped?.toString() || "0") > 0 ? "success" : "warning"} className="fw-normal">
                                                            {line.qty_shipped || 0}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end">
                                                        {line.subprice ? parseFloat(line.subprice.toString()).toFixed(2) + ' €' : '0.00 €'}
                                                    </td>
                                                    <td className="text-end">
                                                        {line.tva_tx || 0}%
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center text-muted py-3">
                                                    Aucune ligne d'article trouvée pour cette expédition
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {expedition.note_public && (
                            <div className="mb-3">
                                <h6 className="border-bottom pb-2 mb-3">
                                    <i className="ri-sticky-note-line me-2"></i>
                                    Note publique
                                </h6>
                                <div className="p-3 bg-light rounded">
                                    {expedition.note_public}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Fermer
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const AllExpeditionsPage = () => {
    const { user } = useAuth();
    
    // States
    const [expeditions, setExpeditions] = useState<Expedition[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [countPerPage, setCountPerPage] = useState(0);
    
    // Filters
    const [searchRef, setSearchRef] = useState("");
    const [searchOrderRef, setSearchOrderRef] = useState("");
    const [searchProductRef, setSearchProductRef] = useState("");
    const [searchStatus, setSearchStatus] = useState<string>("");
    const [searchAll, setSearchAll] = useState("");
    const [searchType, setSearchType] = useState<string>(""); // "", "comptoir", "franchise"
    
    // Stats totals (selon l'API)
    const [totalBrouillon, setTotalBrouillon] = useState(0);
    const [totalValidees, setTotalValidees] = useState(0);
    const [totalCloturees, setTotalCloturees] = useState(0);
    const [totalAnnulees, setTotalAnnulees] = useState(0);
    
    // Error state
    const [error, setError] = useState<string | null>(null);
    
    // Modal
    const [selectedExpedition, setSelectedExpedition] = useState<Expedition | null>(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    
    /* COMMENTÉ: Annulation states */
    // const [cancelModalOpen, setCancelModalOpen] = useState(false);
    // const [expeditionToCancel, setExpeditionToCancel] = useState<Expedition | null>(null);
    // const [cancelling, setCancelling] = useState(false);
    
    // Toast state (optionnel)
    const [toast, setToast] = useState<{
        show: boolean;
        message: string;
        type: 'success' | 'error' | 'info';
    }>({
        show: false,
        message: '',
        type: 'info'
    });

    // Helper function pour les noms de statut
    const getStatusName = (status: number): string => {
        switch (status) {
            case -1: return "Annulée";
            case 0: return "Brouillon";
            case 1: return "Validée";
            case 2: return "Clôturée";
            default: return "Inconnu";
        }
    };

    // Fonction pour afficher un toast
    const showToast = (message: string, type: 'success' | 'error' | 'info') => {
        setToast({
            show: true,
            message,
            type
        });
        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 5000);
    };

    /* COMMENTÉ: Interface et fonction d'annulation
    interface CancelExpeditionResponse extends AxiosResponse<any> {
        success?: boolean;
        message?: string;
        expedition_id?: number;
        ref?: string;
        old_status?: string;
        new_status?: string;
        type?: string;
        stock_movements?: Array<{
            product_id: string;
            product_ref: string;
            product_label: string;
            warehouse_id: string;
            quantity: string;
            batch: boolean;
            movement_id: number;
        }>;
        details?: {
            order_ref: string;
            order_ref_client: string;
            old_status_name: string;
            new_status_name: string;
            cancel_date: string;
            user_id: string;
            user_login: string;
            lines_kept: boolean;
            quantities_zeroed: boolean;
            stock_returned: boolean;
            stock_movement_count: number;
            order_info: {
                order_id: string;
                order_ref: string;
                order_ref_client: string;
                order_type: string;
                order_date: string;
                customer_name: string | null;
            };
        };
    }

    const handleCancelExpedition = async () => {
        if (!expeditionToCancel || !user) return;
        
        setCancelling(true);
        
        try {
            const payload = {
                action: 'cancelexpedition',
                expedition_id: expeditionToCancel.id,
                entity: user.entity
            };
            
            console.log('🔄 Annulation de l\'expédition:', payload);
            
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    },
                }
            );
            
            console.log('📥 Réponse API reçue:', response);
            console.log('📥 Statut HTTP:', response.status);
            console.log('📥 Headers:', response.headers);
            
            const apiResponse = response as CancelExpeditionResponse;
            
            console.log('✅ Données de l\'API:', {
                success: apiResponse.success,
                message: apiResponse.message,
                expedition_id: apiResponse.expedition_id,
                ref: apiResponse.ref
            });
            
            // Traiter la réponse - Vérifier success directement sur response
            if (apiResponse.success) {
                // Rafraîchir la liste des expéditions
                await fetchExpeditions();
                
                // Fermer la modal
                setCancelModalOpen(false);
                setExpeditionToCancel(null);
                
                // Afficher un message de succès personnalisé
                const expeditionRef = apiResponse.ref || expeditionToCancel.ref || 'N/A';
                showToast(`Annulation réussie de l'expédition ${expeditionRef}`, 'success');
                
                // Log supplémentaire
                console.log('🎯 Annulation réussie:', {
                    expedition: apiResponse.ref,
                    status: `${apiResponse.old_status} → ${apiResponse.new_status}`,
                    stockMovements: apiResponse.stock_movements?.length || 0,
                    details: apiResponse.details
                });
            } else {
                // Gérer l'erreur retournée par l'API
                throw new Error(apiResponse.message || 'Erreur lors de l\'annulation');
            }
            
        } catch (error: any) {
            console.error('❌ Erreur détaillée:', error);
            
            // Gestion d'erreur améliorée avec Axios
            let errorMessage = 'Erreur lors de l\'annulation de l\'expédition';
            const expeditionRef = expeditionToCancel?.ref || 'N/A';
            
            if (axios.isAxiosError(error)) {
                // Erreur Axios
                const axiosError = error;
                
                console.error('📡 Erreur Axios:', {
                    code: axiosError.code,
                    message: axiosError.message,
                    response: axiosError.response,
                    status: axiosError.response?.status
                });
                
                if (axiosError.response) {
                    // Le serveur a répondu avec une erreur
                    const errorResponse = axiosError.response as CancelExpeditionResponse;
                    
                    if (errorResponse.message) {
                        // Message d'erreur de l'API
                        errorMessage = `Échec de l'annulation (${expeditionRef}): ${errorResponse.message}`;
                    } else if (typeof errorResponse.data === 'string') {
                        try {
                            const parsedError = JSON.parse(errorResponse.data);
                            errorMessage = `Échec de l'annulation (${expeditionRef}): ${parsedError.message || errorResponse.data}`;
                        } catch {
                            errorMessage = `Échec de l'annulation (${expeditionRef}): ${errorResponse.data}`;
                        }
                    } else if (errorResponse.data && typeof errorResponse.data === 'object') {
                        errorMessage = `Échec de l'annulation (${expeditionRef}): ${errorResponse.data.message || 'Erreur serveur'}`;
                    } else {
                        errorMessage = `Échec de l'annulation (${expeditionRef}): Erreur serveur (${axiosError.response.status})`;
                    }
                } else if (axiosError.request) {
                    // Pas de réponse reçue
                    errorMessage = `Pas de réponse du serveur pour l'annulation de ${expeditionRef}`;
                    console.error('📡 Pas de réponse:', axiosError.request);
                } else {
                    // Erreur de configuration
                    errorMessage = `Erreur de configuration: ${axiosError.message} (${expeditionRef})`;
                }
            } else if (error instanceof Error) {
                // Erreur JavaScript standard
                errorMessage = `Erreur d'annulation (${expeditionRef}): ${error.message}`;
            }
            
            console.error('❌ Message d\'erreur final:', errorMessage);
            showToast(errorMessage, 'error');
            
            // Rafraîchir quand même la liste
            try {
                await fetchExpeditions();
            } catch (refreshError) {
                console.error('⚠️ Impossible de rafraîchir:', refreshError);
            }
            
        } finally {
            // Nettoyage garantie
            setCancelling(false);
        }
    };
    
    // Fonction pour ouvrir la modal de confirmation d'annulation
    const openCancelModal = (expedition: Expedition) => {
        if (expedition.type === 'franchise' && (expedition.status === 1)) {
            setExpeditionToCancel(expedition);
            setCancelModalOpen(true);
        }
    };
    */

    const fetchExpeditions = async (requestedPage?: number) => {
        setLoading(true);
        setError(null);

        try {
            const pageToFetch = requestedPage ?? page;

            const payload: any = {
                action: 'expeditions',
                entity: user.entity,
                page: pageToFetch,
                limit: 100,
            };

            if (searchRef) payload.search_ref = searchRef;
            if (searchOrderRef) payload.search_order_ref = searchOrderRef;
            if (searchProductRef) payload.search_product_ref = searchProductRef;
            if (searchStatus !== "") payload.search_status = searchStatus;
            if (searchAll) payload.sall = searchAll;
            if (searchType) payload.type_filter = searchType;

            // console.log('📤 Fetching expeditions with payload:', payload);

            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    },
                }
            );

            // console.log('📥 API response received:', response.data);
            // console.log('📥 Full response object:', response);
            
            let expeditionsData = [];
            let totalRecords = 0;
            let totalPagesValue = 1;
            let currentPageValue = pageToFetch;
            let countPerPageValue = 0;
            let stats = {
                total_brouillon: 0,
                total_validees: 0,
                total_cloturees: 0,
                total_annulees: 0,
            };

            
            // Définir un type étendu pour la réponse Axios
            interface ExtendedAxiosResponse extends AxiosResponse {
                nbtotalofrecords?: number;
                totalpages?: number;
                currentpage?: number;
                count?: number;
                total?: number;
                stats?: {
                    total_brouillon: number;
                    total_validees: number;
                    total_cloturees: number;
                    total_annulees: number;
                };
                success?: boolean;
                message?: string;
            }

           
            const apiResponse = response as ExtendedAxiosResponse;
            
            
            const responseData = apiResponse.data;
            
            if (responseData && typeof responseData === 'object') {
                if (Array.isArray(responseData)) {
                    
                    expeditionsData = responseData;
                    countPerPageValue = expeditionsData.length;
                    
                    
                    totalRecords = apiResponse.nbtotalofrecords || 0;
                    totalPagesValue = apiResponse.totalpages || 1;
                    currentPageValue = apiResponse.currentpage || pageToFetch;
                    
                   
                    if (apiResponse.nbtotalofrecords || apiResponse.totalpages) {
                        console.log('📊 Using metadata from response object:', {
                            totalRecords,
                            totalPagesValue,
                            currentPageValue,
                            source: 'response metadata'
                        });
                    } else {
                        
                        const hasMorePages = expeditionsData.length === 100;
                        totalRecords = expeditionsData.length;
                        totalPagesValue = hasMorePages ? pageToFetch + 1 : pageToFetch;
                        
                        console.log('📈 Estimated pagination (no metadata):', {
                            totalRecords,
                            totalPagesValue,
                            hasMorePages,
                            source: 'estimated'
                        });
                    }
                    
                    
                    if (apiResponse.stats) {
                        stats = apiResponse.stats;
                    } else {
                        
                        stats = {
                            total_brouillon: expeditionsData.filter(exp => Number(exp.status) === 0).length,
                            total_validees: expeditionsData.filter(exp => Number(exp.status) === 1).length,
                            total_cloturees: expeditionsData.filter(exp => Number(exp.status) === 2).length,
                            total_annulees: expeditionsData.filter(exp => Number(exp.status) === -1).length,
                        };
                    }
                }
                else if (responseData.data && Array.isArray(responseData.data)) {
                    
                    expeditionsData = responseData.data;
                    totalRecords = responseData.nbtotalofrecords || responseData.total || 0;
                    totalPagesValue = responseData.totalpages || 1;
                    currentPageValue = responseData.currentpage || pageToFetch;
                    countPerPageValue = responseData.count || expeditionsData.length;
                    stats = responseData.stats || stats;
                    
                    // console.log('📊 Using structured data in response.data:', {
                    //     totalRecords,
                    //     totalPagesValue,
                    //     currentPageValue,
                    //     source: 'response.data structured'
                    // });
                }
                else if (responseData.success !== undefined) {
                    // FORMAT 3: Objet dans response.data
                    if (responseData.success === false) {
                        throw new Error(responseData.message || 'API returned an error');
                    }
                    
                    expeditionsData = [responseData];
                    totalRecords = responseData.nbtotalofrecords || responseData.total || 1;
                    totalPagesValue = responseData.totalpages || 1;
                    currentPageValue = responseData.currentpage || pageToFetch;
                    countPerPageValue = 1;
                    stats = responseData.stats || stats;
                    
                    // console.log('📊 Using response.data object as data:', {
                    //     totalRecords,
                    //     totalPagesValue,
                    //     currentPageValue,
                    //     source: 'response.data object'
                    // });
                }
            } else {
                throw new Error('Format de réponse API invalide');
            }
            
            // console.log(`📊 Processing ${expeditionsData.length} expeditions for page ${currentPageValue}`);

            // Formatter les expéditions
            const formattedExpeditions = expeditionsData.map((exp: any, index: number) => {
                // Gestion des lignes
                let lines: ExpeditionLine[] = [];
                
                if (exp.lines && Array.isArray(exp.lines)) {
                    lines = exp.lines.map((line: any, lineIndex: number) => ({
                        id: Number(line.id) || Number(line.line_id) || lineIndex,
                        product_id: Number(line.product_id) || Number(line.fk_product) || 0,
                        ref: line.ref || line.product_ref || line.reference || '',
                        libelle: line.libelle || line.label || line.product_label || '',
                        qty: Number(line.qty) || Number(line.quantity) || 0,
                        qty_shipped: Number(line.qty_shipped) || Number(line.quantity_shipped) || 0,
                        subprice: Number(line.subprice) || Number(line.price) || 0,
                        tva_tx: Number(line.tva_tx) || Number(line.vat_rate) || 0,
                        origin_line_id: Number(line.origin_line_id) || Number(line.fk_origin_line) || 0,
                    }));
                }

                const status = Number(exp.status ?? exp.fk_statut ?? 0);
                const statusName = exp.status_name || getStatusName(status);

                return {
                    id: Number(exp.id) || 0,
                    ref: exp.ref || '',
                    status,
                    status_name: statusName,
                    date_creation: exp.date_creation || '',
                    date_valid: exp.date_valid || '',
                    date_delivery: exp.date_delivery || '',
                    date_expedition: exp.date_expedition || '',
                    socid: Number(exp.socid) || 0,
                    company_name: exp.company_name || '',
                    code_client: exp.code_client || '',
                    order_id: Number(exp.order_id) || 0,
                    order_ref: exp.order_ref || '',
                    order_ref_customer: exp.order_ref_customer || '',
                    order_date: exp.order_date || '',
                    user_login: exp.user_login || '',
                    nb_lines: Number(exp.nb_lines) || lines.length,
                    note_public: exp.note_public || '',
                    note_private: exp.note_private || '',
                    lines,
                    orderinfo: exp.orderinfo || {
                        orderid: Number(exp.order_id) || 0,
                        ref: exp.order_ref || '',
                        ref_client: exp.order_ref_customer || '',
                        client_name: exp.company_name || '',
                        date_creation: exp.order_date || ''
                    },
                    type: exp.type || ((exp.order_ref_customer && exp.order_ref_customer.trim() !== '') ? 'franchise' : 'comptoir')
                };
            });

            // Mettre à jour l'état
            setExpeditions(formattedExpeditions);
            setTotalCount(totalRecords);
            setTotalPages(totalPagesValue);
            setCountPerPage(countPerPageValue);
            setPage(currentPageValue);

            setTotalBrouillon(stats.total_brouillon || 0);
            setTotalValidees(stats.total_validees || 0);
            setTotalCloturees(stats.total_cloturees || 0);
            setTotalAnnulees(stats.total_annulees || 0);

            // console.log(`✅ Successfully loaded ${formattedExpeditions.length} expeditions`, {
            //     page: currentPageValue,
            //     totalPages: totalPagesValue,
            //     totalRecords,
            //     pagination: `${currentPageValue}/${totalPagesValue}`,
            //     hasMorePages: currentPageValue < totalPagesValue,
            //     stats,
            //     source: totalPagesValue === (pageToFetch + 1) ? 'estimated' : 'from API'
            // });

        } catch (error: any) {
            console.error('❌ Error fetching expeditions:', error);
            
            // Log détaillé
            if (error.response) {
                console.error('Error response status:', error.response.status);
                console.error('Full error response:', error.response);
            }
            
            let errorMessage = 'Erreur de chargement des expéditions';
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'string') {
                    errorMessage = `Serveur: ${error.response.data.substring(0, 200)}`;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setError(`Erreur : ${errorMessage}`);

            // Réinitialiser
            setExpeditions([]);
            setTotalCount(0);
            setTotalPages(1);
            setCountPerPage(0);
            setPage(1);

            setTotalBrouillon(0);
            setTotalValidees(0);
            setTotalCloturees(0);
            setTotalAnnulees(0);

        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        fetchExpeditions();
    };

    // Clear filters
    const clearFilters = () => {
        setSearchRef("");
        setSearchOrderRef("");
        setSearchProductRef("");
        setSearchStatus("");
        setSearchAll("");
        setSearchType("");
        setPage(1);
        setError(null);
        fetchExpeditions();
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            fetchExpeditions(newPage);
        }
    };

    // Open details modal
    const openDetailsModal = (expedition: Expedition) => {
        setSelectedExpedition(expedition);
        setDetailsModalOpen(true);
    };

    // Réinitialiser la page quand les filtres changent
    useEffect(() => {
        setPage(1);
    }, [searchRef, searchOrderRef, searchProductRef, searchStatus, searchType, searchAll]);

    // Fetch data quand la page ou les filtres changent
    useEffect(() => {
        if (user && user.token && user.entity) {
            fetchExpeditions();
        } else {
            console.error('User not authenticated or missing token/entity');
            setError('Utilisateur non authentifié');
        }
    }, [page, searchRef, searchOrderRef, searchProductRef, searchStatus, searchType, searchAll]);

    // Table columns
    const columns = [
        {
            header: "Référence",
            accessorKey: "ref",
            cell: (cell: any) => {
                const expedition = cell.row.original;
                return (
                    <div>
                        <div 
                            className="fw-semibold text-primary cursor-pointer"
                            onClick={() => openDetailsModal(expedition)}
                            style={{ cursor: 'pointer' }}
                        >
                            {expedition.ref || 'N/A'}
                        </div>
                        <small className="text-muted">
                            {expedition.type === 'comptoir' ? 'Comptoir' : 'Franchise'}
                        </small>
                    </div>
                );
            }
        },
        {
            header: "Statut",
            accessorKey: "status",
            cell: (cell: any) => {
                const status = parseInt(cell.getValue());
                switch (status) {
                    case 0:
                        return <Badge color="secondary">Brouillon</Badge>;
                    case 1:
                        return <Badge color="success">Validée</Badge>;
                    case 2:
                        return <Badge color="info">Clôturée</Badge>;
                    case -1:
                        return <Badge color="danger">Annulée</Badge>;
                    default:
                        return <Badge color="secondary">Inconnu</Badge>;
                }
            }
        },
        {
            header: "Commande",
            accessorKey: "orderinfo",
            cell: (cell: any) => {
                const orderinfo = cell.getValue();
                return (
                    <div>
                        <div className="fw-semibold">{orderinfo?.ref || 'N/A'}</div>
                        <small className="text-muted">
                            {orderinfo?.ref_client ? `Ref client: ${orderinfo.ref_client}` : 'Comptoir'}
                        </small>
                    </div>
                );
            }
        },
        {
            header: "Client",
            accessorKey: "company_name",
            cell: (cell: any) => {
                const expedition = cell.row.original;
                return (
                    <div>
                        <div className="fw-semibold">{expedition.company_name || 'N/A'}</div>
                        <small className="text-muted">{expedition.code_client || ''}</small>
                    </div>
                );
            }
        },
        {
            header: "Articles",
            accessorKey: "nb_lines",
            cell: (cell: any) => {
                const expedition = cell.row.original;
                const totalShipped = expedition.lines?.reduce((sum: number, line: ExpeditionLine) => 
                    sum + (parseFloat(line.qty_shipped?.toString()) || 0), 0) || 0;
                return (
                    <div className="text-right w-100" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'flex-middle',
                        justifyContent: 'right',
                        height: '100%'
                    }}>
                        <div className="fw-bold" style={{paddingLeft:'10px'}}>{expedition.nb_lines || 0}</div>
                        <small className="text-muted">ligne(s)</small>
                        <div className="small mt-1">
                            <Badge color={totalShipped > 0 ? "success" : "warning"} className="fw-normal">
                                {totalShipped} expédié(s)
                            </Badge>
                        </div>
                    </div>
                );
            }
        },
        {
            header: "Actions",
            accessorKey: "id",
            cell: (cell: any) => {
                const expedition = cell.row.original;
                /* COMMENTÉ: Logique d'annulation
                const isFranchise = expedition.type === 'franchise';
                const isDraftOrValidated = expedition.status === 1;
                const canCancel = isFranchise && isDraftOrValidated;
                */
                
                return (
                    <div className="d-flex gap-1">
                        <Button
                            color="light"
                            size="sm"
                            onClick={() => openDetailsModal(expedition)}
                            title="Voir les détails"
                        >
                            <i className="ri-eye-line"></i>
                        </Button>
                        
                        {/* COMMENTÉ: Bouton d'annulation
                        {isFranchise ? (
                            canCancel ? (
                                <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => openCancelModal(expedition)}
                                    title="Annuler cette expédition"
                                    className="ms-1"
                                >
                                    <i className="ri-close-line"></i>
                                </Button>
                            ) : (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    disabled
                                    title={expedition.status === 2 ? "Expédition déjà clôturée" : 
                                           expedition.status === -1 ? "Expédition déjà annulée" : 
                                           "Non annulable"}
                                    className="ms-1"
                                >
                                    <i className="ri-close-line"></i>
                                </Button>
                            )
                        ) : null}
                        */}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="page-content">
            <Container fluid>
                {/* Toast Notification */}
                {toast.show && (
                    <div className={`toast-container position-fixed top-0 end-0 p-3`} style={{ zIndex: 9999 }}>
                        <div className={`toast show align-items-center text-bg-${toast.type} border-0`} role="alert">
                            <div className="d-flex">
                                <div className="toast-body">
                                    <i className={`ri-${toast.type === 'success' ? 'check-line' : toast.type === 'error' ? 'close-circle-line' : 'information-line'} me-2`}></i>
                                    {toast.message}
                                </div>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white me-2 m-auto" 
                                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                                ></button>
                            </div>
                        </div>
                    </div>
                )}
                
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h4 className="card-title mb-0">
                                            <i className="ri-truck-line align-bottom me-2"></i>
                                            Toutes les expéditions
                                        </h4>
                                        <p className="text-muted mb-0">
                                            {loading ? 'Chargement...' : `${totalCount.toLocaleString()} expéditions au total • ${totalPages} pages`}
                                        </p>
                                    </div>
                                </div>

                                {/* Afficher les erreurs */}
                                {error && (
                                    <div className="alert alert-danger mb-4" role="alert">
                                        <i className="ri-alert-line me-2"></i>
                                        <strong>Erreur:</strong> {error}
                                        <Button 
                                            color="link" 
                                            size="sm" 
                                            className="p-0 ms-2"
                                            onClick={() => setError(null)}
                                        >
                                            <i className="ri-close-line"></i>
                                        </Button>
                                    </div>
                                )}

                                {/* Search and Filters */}
                                <form onSubmit={handleSearch} className="mb-4">
                                    <Row className="g-3 align-items-end">
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="searchRef" className="form-label small text-muted mb-1">
                                                    RÉFÉRENCE EXPÉDITION
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-barcode-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        id="searchRef"
                                                        placeholder="SH2601-5154..."
                                                        value={searchRef}
                                                        onChange={(e) => setSearchRef(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="searchOrderRef" className="form-label small text-muted mb-1">
                                                    RÉFÉRENCE COMMANDE
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-shopping-cart-2-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        id="searchOrderRef"
                                                        placeholder="CO2601-30443..."
                                                        value={searchOrderRef}
                                                        onChange={(e) => setSearchOrderRef(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="searchStatus" className="form-label small text-muted mb-1">
                                                    STATUT
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id="searchStatus"
                                                    value={searchStatus}
                                                    onChange={(e) => setSearchStatus(e.target.value)}
                                                    style={{ height: "42px" }}
                                                >
                                                    <option value="">Tous les statuts</option>
                                                    <option value="0">Brouillon</option>
                                                    <option value="1">Validée</option>
                                                    <option value="2">Clôturée</option>
                                                    <option value="-1">Annulée</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="searchType" className="form-label small text-muted mb-1">
                                                    TYPE
                                                </Label>
                                                <Input
                                                    type="select"
                                                    id="searchType"
                                                    value={searchType}
                                                    onChange={(e) => setSearchType(e.target.value)}
                                                    style={{ height: "42px" }}
                                                >
                                                    <option value="">Tous les types</option>
                                                    <option value="comptoir">Comptoir</option>
                                                    <option value="franchise">Franchise</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="searchProductRef" className="form-label small text-muted mb-1">
                                                    RÉF PRODUIT
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-search-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        type="text"
                                                        id="searchProductRef"
                                                        placeholder="BPX123..."
                                                        value={searchProductRef}
                                                        onChange={(e) => setSearchProductRef(e.target.value)}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        {/* Nouveau Col pour les boutons alignés */}
                                        <Col md={2}>
                                            <FormGroup>
                                                {/* Label invisible pour garder l'alignement */}
                                                <Label className="form-label small text-muted mb-1" style={{ visibility: "hidden" }}>
                                                    Actions
                                                </Label>
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button
                                                        type="submit"
                                                        color="primary"
                                                        className="px-4"
                                                        style={{ height: "42px" }}
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <Spinner size="sm" />
                                                        ) : (
                                                            <>
                                                                <i className="ri-search-line align-bottom me-1"></i>
                                                                Rechercher
                                                            </>
                                                        )}
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        color="warning"
                                                        style={{ 
                                                            height: "42px",
                                                            width: "42px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}
                                                        onClick={clearFilters}
                                                        disabled={loading}
                                                        title="Effacer les filtres"
                                                    >
                                                        <i className="ri-eraser-line"></i>
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </form>

                                {/* Stats Summary */}
                                <Row className="mb-4 g-2">
                                    <Col md={3}>
                                        <Card className="card-body bg-light p-3 h-100 d-flex align-items-center justify-content-center">
                                            <div className="d-flex align-items-center">
                                                <div className="rounded-circle bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                                                    <i className="ri-draft-line fs-5"></i>
                                                </div>
                                                <div className="d-flex align-items-baseline">
                                                    <h5 className="mb-0 fw-bold me-2">{totalBrouillon.toLocaleString()}</h5>
                                                    <span className="text-muted">Brouillons</span>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                    
                                    <Col md={3}>
                                        <Card className="card-body bg-light p-3 h-100 d-flex align-items-center justify-content-center">
                                            <div className="d-flex align-items-center">
                                                <div className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                                                    <i className="ri-check-line fs-5"></i>
                                                </div>
                                                <div className="d-flex align-items-baseline">
                                                    <h5 className="mb-0 fw-bold me-2">{totalValidees.toLocaleString()}</h5>
                                                    <span className="text-muted">Validées</span>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                    
                                    <Col md={3}>
                                        <Card className="card-body bg-light p-3 h-100 d-flex align-items-center justify-content-center">
                                            <div className="d-flex align-items-center">
                                                <div className="rounded-circle bg-info-subtle text-info d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                                                    <i className="ri-check-double-line fs-5"></i>
                                                </div>
                                                <div className="d-flex align-items-baseline">
                                                    <h5 className="mb-0 fw-bold me-2">{totalCloturees.toLocaleString()}</h5>
                                                    <span className="text-muted">Clôturées</span>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                    
                                    <Col md={3}>
                                        <Card className="card-body bg-light p-3 h-100 d-flex align-items-center justify-content-center">
                                            <div className="d-flex align-items-center">
                                                <div className="rounded-circle bg-danger-subtle text-danger d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                                                    <i className="ri-close-line fs-5"></i>
                                                </div>
                                                <div className="d-flex align-items-baseline">
                                                    <h5 className="mb-0 fw-bold me-2">{totalAnnulees.toLocaleString()}</h5>
                                                    <span className="text-muted">Annulées</span>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                               
                                {/* Table */}
                                {loading && expeditions.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" />
                                        <div className="mt-2">Chargement des expéditions...</div>
                                    </div>
                                ) : (
                                    <>
                                        {expeditions.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ri-inbox-line display-4 text-muted"></i>
                                                <h5 className="mt-3">Aucune expédition trouvée</h5>
                                                <p className="text-muted">
                                                    {searchRef || searchOrderRef || searchProductRef || searchStatus || searchType || searchAll ? 
                                                        "Aucune expédition ne correspond à vos critères de recherche." :
                                                        "Aucune expédition n'a été trouvée dans la base de données."
                                                    }
                                                </p>
                                                {(searchRef || searchOrderRef || searchProductRef || searchStatus || searchType || searchAll) && (
                                                    <Button
                                                        color="primary"
                                                        onClick={clearFilters}
                                                        className="mt-2"
                                                    >
                                                        <i className="ri-refresh-line me-1"></i>
                                                        Réinitialiser les filtres
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="position-relative">
                                                    {loading && (
                                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                                                            <div className="text-center">
                                                                <Spinner color="primary" />
                                                                <div className="mt-2">Chargement...</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <TableContainer
                                                        columns={columns}
                                                        data={expeditions}
                                                        isGlobalFilter={false}
                                                        customPageSize={countPerPage}
                                                        divClass="table-responsive"
                                                        tableClass="align-middle table-nowrap"
                                                        theadClass="table-light text-muted"
                                                    />
                                                </div>

                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <SmartPagination
                                                        currentPage={page}
                                                        totalPages={totalPages}
                                                        totalCount={totalCount}
                                                        countPerPage={countPerPage}
                                                        onPageChange={handlePageChange}
                                                        loading={loading}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Expedition Details Modal */}
            <ExpeditionDetailsModal
                isOpen={detailsModalOpen}
                toggle={() => setDetailsModalOpen(false)}
                expedition={selectedExpedition}
            />

            {/* COMMENTÉ: Cancel Confirmation Modal
            <Modal isOpen={cancelModalOpen} toggle={() => setCancelModalOpen(false)}>
                <ModalHeader toggle={() => setCancelModalOpen(false)}>
                    <i className="ri-alert-line me-2 text-danger"></i>
                    Confirmer l'annulation
                </ModalHeader>
                <ModalBody>
                    {expeditionToCancel && (
                        <div>
                            <div className="alert alert-danger" role="alert">
                                <i className="ri-error-warning-line me-2"></i>
                                <strong>Attention !</strong> Cette action est irréversible.
                            </div>
                            
                            <p>
                                Êtes-vous sûr de vouloir annuler l'expédition <strong>{expeditionToCancel.ref}</strong> ?
                            </p>
                            
                            <div className="border rounded p-3 bg-light">
                                <div className="row">
                                    <div className="col-6">
                                        <small className="text-muted">Client</small>
                                        <div className="fw-semibold">{expeditionToCancel.company_name}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Commande associée</small>
                                        <div className="fw-semibold">{expeditionToCancel.order_ref}</div>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-6">
                                        <small className="text-muted">Type</small>
                                        <div>
                                            <Badge color="warning">Franchise</Badge>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Statut actuel</small>
                                        <div>
                                            {expeditionToCancel.status === 0 ? (
                                                <Badge color="secondary">Brouillon</Badge>
                                            ) : (
                                                <Badge color="success">Validée</Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="secondary" 
                        onClick={() => setCancelModalOpen(false)}
                        disabled={cancelling}
                    >
                        Annuler
                    </Button>
                    <Button 
                        color="danger" 
                        onClick={handleCancelExpedition}
                        disabled={cancelling}
                    >
                        {cancelling ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Annulation...
                            </>
                        ) : (
                            <>
                                <i className="ri-close-line me-1"></i>
                                Confirmer l'annulation
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
            */}
        </div>
    );
};

export default AllExpeditionsPage;